import { Field, Label, Textarea } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import clsx from 'clsx'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { object, string } from 'yup'
import { useTestLlmClassificationPromptsLazyQuery } from '../api/client'
import Button from '../components/Button'
import FormFieldError from '../components/errors/FormFieldError'
import { toastError } from '../components/Notification'
import SimpleLoader from '../components/SimpleLoader'
import PromptResultsTable from '../components/prompt/PromptResultsTable'
import { DEFAULT_LLM_MODEL, LLM_MODELS, getLlmModelLabel } from '../models/LlmModel'
import { parseMessages, PromptResult, PromptTestCase } from '../util/promptTesting'

const PROMPT_MESSAGE_SUBSTITUTION_VARIABLE_REGEX = /<<<PLACEHOLDER MESSAGE>>>/
const LOCAL_STORAGE_PROMPT_MESSAGES = 'LOCAL_STORAGE_PROMPT_MESSAGES'
const LOCAL_STORAGE_PROMPT = 'LOCAL_STORAGE_PROMPT'
const LOCAL_STORAGE_MODEL = 'LOCAL_STORAGE_PROMPT_MODEL'
const LOCAL_STORAGE_MODERATION_BOUNDARY = 'LOCAL_STORAGE_MODERATION_BOUNDARY'
const DEFAULT_MODERATION_BOUNDARY = 0.75

const validationPromptSchema = object({
    prompt: string()
        .required('Please enter a prompt')
        .matches(
            PROMPT_MESSAGE_SUBSTITUTION_VARIABLE_REGEX,
            'Prompt must include the substitution variable <<<PLACEHOLDER MESSAGE>>>'
        ),

    messages: string().required('Please enter a prompt'),

    model: string().required('Please select an LLM model')
})

export default function PromptTestingPage() {
    const initialMessages = localStorage.getItem(LOCAL_STORAGE_PROMPT_MESSAGES) || ''
    const initialPrompt = localStorage.getItem(LOCAL_STORAGE_PROMPT) || ''
    const initialModel = localStorage.getItem(LOCAL_STORAGE_MODEL) || DEFAULT_LLM_MODEL
    const initialModerationBoundary = parseFloat(
        localStorage.getItem(LOCAL_STORAGE_MODERATION_BOUNDARY) ||
            String(DEFAULT_MODERATION_BOUNDARY)
    )
    const [promptResults, setPromptResults] = useState<PromptResult[]>([])
    const [parsedMessages, setParsedMessages] = useState<PromptTestCase[]>([])
    const [testingPromptsLoading, setTestingPromptsLoading] = useState(false)
    const [processedCount, setProcessedCount] = useState(0)
    const [moderationBoundary, setModerationBoundary] = useState(initialModerationBoundary)
    const [rerunningText, setRerunningText] = useState<string | null>(null)

    const [testLlmClassificationPrompts] = useTestLlmClassificationPromptsLazyQuery()

    const handleOnValidate = async () => {
        const messages = promptForm.getValues().messages
        // This is where we define form validation for the test message.
        if (!messages || messages === '') {
            promptForm.setError('messages', {
                message: 'Please enter messages for testing. See advice.'
            })
        } else {
            const parsedMessages = parseMessages(messages)
            if (parsedMessages) {
                setParsedMessages(parsedMessages)
            }
        }
    }

    const handleOnTest = async () => {
        const prompt = promptForm.getValues().prompt
        const messages = promptForm.getValues().messages
        const model = promptForm.getValues().model
        // This is where we define form validation for the test message.
        if (!messages || messages === '') {
            promptForm.setError('messages', {
                message: 'Please enter messages for testing. See advice.'
            })
        } else {
            setPromptResults([])
            setProcessedCount(0)

            if (parsedMessages && parsedMessages.length > 0) {
                setTestingPromptsLoading(true)
                try {
                    const testCases = parsedMessages.map((m) => ({
                        text: m.text,
                        target: m.target
                    }))
                    const { data } = await testLlmClassificationPrompts({
                        variables: { prompt, testCases, model }
                    })
                    if (data) {
                        const results: PromptResult[] = data.testLlmClassificationPrompts.map(
                            (res) => ({
                                text: res.text,
                                target: res.target as 'Yes' | 'No',
                                score: res.score ?? null,
                                justification: res.justification
                            })
                        )
                        setPromptResults(results)
                        setProcessedCount(results.length)
                    }
                } catch (error) {
                    console.error('Error testing LLM classification prompt:', error)
                    toastError('Failed to test prompts')
                } finally {
                    setTestingPromptsLoading(false)
                }
            }
        }
    }

    const handleRerunSingle = async (text: string) => {
        const prompt = promptForm.getValues().prompt
        const model = promptForm.getValues().model

        const original = promptResults.find((r) => r.text === text)
        if (!original) return

        setRerunningText(text)
        try {
            const testCases = [{ text, target: original.target }]
            const { data } = await testLlmClassificationPrompts({
                variables: { prompt, testCases, model }
            })
            if (data && data.testLlmClassificationPrompts.length > 0) {
                const res = data.testLlmClassificationPrompts[0]
                const newResult: PromptResult = {
                    text: res.text,
                    target: res.target as 'Yes' | 'No',
                    score: res.score ?? null,
                    justification: res.justification
                }
                setPromptResults((prev) => prev.map((r) => (r.text === text ? newResult : r)))
            }
        } catch (error) {
            console.error('Error re-running classification:', error)
            toastError('Failed to re-run classification')
        } finally {
            setRerunningText(null)
        }
    }

    const promptForm = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationPromptSchema),
        defaultValues: {
            prompt: initialPrompt,
            messages: initialMessages,
            model: initialModel
        }
    })

    const messages = promptForm.getValues().messages
    const prompt = promptForm.getValues().prompt
    const model = promptForm.getValues().model

    // Save to localStorage
    localStorage.setItem(LOCAL_STORAGE_PROMPT_MESSAGES, messages)
    localStorage.setItem(LOCAL_STORAGE_PROMPT, prompt)
    localStorage.setItem(LOCAL_STORAGE_MODEL, model)
    localStorage.setItem(LOCAL_STORAGE_MODERATION_BOUNDARY, String(moderationBoundary))

    return (
        <div>
            <div className="prose prose-stone">
                <div>
                    <FormProvider {...promptForm}>
                        <form>
                            <Field className="mt-6">
                                <Label className="block font-medium leading-6 text-gray-90">
                                    Prompt
                                </Label>

                                <Textarea
                                    {...promptForm.register('prompt')}
                                    className={clsx(
                                        'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow',
                                        'ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 ',
                                        'focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6'
                                    )}
                                    rows={20}
                                />
                                {promptForm.formState.errors.prompt && (
                                    <FormFieldError
                                        message={promptForm.formState.errors.prompt.message || ''}
                                    />
                                )}
                            </Field>
                            <Field className="mt-6">
                                <Label className="block font-medium leading-6 text-gray-90">
                                    Model
                                </Label>
                                <select
                                    {...promptForm.register('model')}
                                    className={clsx(
                                        'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow',
                                        'ring-1 ring-inset ring-gray-300 placeholder:text-gray-400',
                                        'focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6'
                                    )}
                                >
                                    {LLM_MODELS.map((llmModel) => (
                                        <option key={llmModel.id} value={llmModel.id}>
                                            {getLlmModelLabel(llmModel)}
                                            {llmModel.isDefault ? ' (default)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                            <Field className="mt-10">
                                <Label className="block font-medium leading-6 text-gray-90">
                                    Test script
                                </Label>

                                <Textarea
                                    {...promptForm.register('messages', {})}
                                    className={clsx(
                                        'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow',
                                        'ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 ',
                                        'focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6'
                                    )}
                                    rows={20}
                                />
                                {promptForm.formState.errors.messages && (
                                    <FormFieldError
                                        message={promptForm.formState.errors.messages.message || ''}
                                    />
                                )}
                            </Field>
                            <div className="prose-p text-sm">
                                <p>
                                    The test script needs to have a test message per line. The
                                    message should have a trailing comma and the expected result
                                    after that as either "Yes" or "No". The following are all valid:
                                </p>
                                <ul>
                                    <li>You are right this is super, Yes</li>
                                    <li>"You're correct", "Yes"</li>
                                </ul>
                                <p>
                                    If you change the test data you must click "Validate" before
                                    running the test again. Review the validated test data in the
                                    "Test Data section below"
                                </p>
                            </div>

                            <div className="flex flex-row gap-4">
                                <div className="mt-2">
                                    <Button
                                        primary={true}
                                        text="Validate"
                                        onClick={handleOnValidate}
                                    ></Button>
                                </div>
                                <div className="mt-2">
                                    <Button
                                        primary={true}
                                        text="Test"
                                        onClick={handleOnTest}
                                        disabled={!parsedMessages || parsedMessages.length === 0}
                                    ></Button>
                                </div>
                            </div>

                            {testingPromptsLoading && (
                                <div className="flex items-center gap-2 my-4">
                                    <SimpleLoader loading={true} />
                                    <span>
                                        {processedCount}/{parsedMessages.length} (
                                        {Math.round((processedCount / parsedMessages.length) * 100)}
                                        %)
                                    </span>
                                </div>
                            )}

                            <div>
                                <h2>Test data</h2>
                                <p>Validated {parsedMessages.length} rows of test data</p>
                                {/* <table className="table-auto">
                                    <thead>
                                        <tr>
                                            <th>Text</th>
                                            <th>Target</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parsedMessages &&
                                            parsedMessages.map((message) => {
                                                return (
                                                    <tr key={message.text} className="">
                                                        <td>{message.text}</td>
                                                        <td>{message.target} </td>
                                                    </tr>
                                                )
                                            })}
                                    </tbody>
                                </table> */}
                            </div>
                        </form>
                    </FormProvider>
                </div>
            </div>

            {/* Results table full width */}
            <div className="w-full px-10">
                <PromptResultsTable
                    promptResults={promptResults}
                    moderationBoundary={moderationBoundary}
                    onModerationBoundaryChange={setModerationBoundary}
                    onRerunSingle={handleRerunSingle}
                    rerunningText={rerunningText}
                />
            </div>
        </div>
    )
}
